import { signal } from '@angular/core';
import { RAD_STRINGS } from './strings';

const FALLBACK_LANG = 'en';
const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;

// What an application has registered, kept apart from the shipped strings so that RAD_STRINGS stays
// the record of what the library ships and a registration is visibly additive.
const registered: Record<string, Record<string, string>> = {};

// The language every RAD component reads, seeded from the document so that a component dropped onto
// a page with no wiring at all still speaks that page's language.
const currentLang = signal(document.documentElement.lang || FALLBACK_LANG);

// A tag is matched whole before its primary subtag, which is what lets pt-BR carry its own wording
// over the pt the library ships. English is the last answer before the key itself.
function findText(key: string, lang: string): string {
    const primary = lang.split('-')[0].toLowerCase();
    return registered[lang]?.[key]
        ?? registered[primary]?.[key]
        ?? RAD_STRINGS[key]?.[lang]
        ?? RAD_STRINGS[key]?.[primary]
        ?? registered[FALLBACK_LANG]?.[key]
        ?? RAD_STRINGS[key]?.[FALLBACK_LANG];
}

function interpolate(text: string, params: Record<string, string | number>): string {
    return text.replace(PLACEHOLDER, (placeholder, name) => String(params?.[name] ?? placeholder));
}

// RAD's own translations. The library carries them rather than reading the application's, because it
// ships without one — so this holds the strings, the language, and the way in for an application that
// wants to word something differently. The names are TranslateService's, since that is what the rest
// of the product calls.
export const i18n = {

    /** The language in use, as a BCP 47 tag. A signal, so a component reading it re-renders when it moves. */
    currentLang,

    /** Set the language. The application calls this wherever it sets its own. */
    use(lang: string): void {
        currentLang.set(lang || FALLBACK_LANG);
    },

    /**
     * The string a key holds in the current language, with `{{ name }}` placeholders filled from `params`.
     * An unknown key is returned as itself, and says so on the console.
     */
    instant(key: string, params?: Record<string, string | number>): string {
        const text = findText(key, currentLang());
        if (text === undefined) {
            console.warn(`rad-ui: no string for ${key}`);
            return key;
        }
        return interpolate(text, params);
    },

    /**
     * Add or replace strings for one language, merged over what the library ships. This is how an
     * application words something differently, adds a language RAD has no strings for, or routes RAD
     * text through its own translations.
     */
    setTranslation(lang: string, strings: Record<string, string>): void {
        registered[lang] = { ...registered[lang], ...strings };
    },
};
